import type { OrganizationKey } from '../types/organizations';
import {
  DEFAULT_ORGANIZATION,
  recordMatchesOrg,
  resolveOrgKey,
  tagWithOrg,
  userOrgScopedStorageKey
} from '../types/organizations';

export type OrgScopedRecord = { orgKey?: string };

export const filterRecordsByOrg = <T extends OrgScopedRecord>(
  records: T[],
  orgKey: OrganizationKey
): T[] => records.filter((record) => recordMatchesOrg(record, orgKey));

export const migrateRecordOrgKey = <T extends OrgScopedRecord>(record: T): T & { orgKey: OrganizationKey } => {
  return tagWithOrg(record, resolveOrgKey(record.orgKey));
};

export const STORAGE_KEYS = {
  scanHistory: 'raedar_scan_history',
  activeScanId: 'raedar_active_scan_id',
  scanResults: 'raedar_scan_results',
  allProspects: 'raedar_all_prospects',
  callLogs: 'raedar_call_logs',
  savedProspects: 'raedar_saved_prospects',
  activeSheet: 'raedar_active_sheet'
} as const;

export const orgStorageKey = (
  base: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS],
  userId: string,
  orgKey: OrganizationKey
): string => userOrgScopedStorageKey(base, userId, orgKey);

export const readOrgScopedJson = <T>(
  base: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS],
  userId: string,
  orgKey: OrganizationKey
): T | null => {
  try {
    const scopedRaw = localStorage.getItem(orgStorageKey(base, userId, orgKey));
    if (scopedRaw) return JSON.parse(scopedRaw) as T;

    if (orgKey !== DEFAULT_ORGANIZATION) return null;

    const legacyRaw = localStorage.getItem(`${base}_${userId}`);
    if (!legacyRaw) return null;
    return JSON.parse(legacyRaw) as T;
  } catch {
    return null;
  }
};

export const writeOrgScopedJson = (
  base: (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS],
  userId: string,
  orgKey: OrganizationKey,
  value: unknown
): void => {
  localStorage.setItem(orgStorageKey(base, userId, orgKey), JSON.stringify(value));
};
